import { Component, Element, h, Host, Prop, Watch } from "@stencil/core";

import { StzhEventinfoItem, StzhEventinfoItemInput, StzhEventinfoMonth, StzhLocaleAdapter } from "../../../index";

import { StzhEventinfoLocalizedText } from "./stzh-eventinfo.localization";
import { setPropsIfNull } from "../../../utils/utils";

/**
 * @slot location - stzh-text for location
 * @slot additional-info - stzh-text for additional info
 */
@Component({
  tag: "stzh-eventinfo",
  styleUrl: "stzh-eventinfo.scss",
  scoped: true,
})
export class StzhEventinfo {
  /** Translation strings */
  @Prop() localization: StzhEventinfoLocalizedText;

  /** Month heading level */
  @Prop() monthHeadingLevel: "1" | "2" | "3" | "4" = "4";

  /** Start date */
  @Prop() start: Date | string;

  /** End date */
  @Prop() end: Date | string;

  /** Calendar URL */
  @Prop() calendarUrl: string;

  /** Calendar label */
  @Prop() calendarLabel: string;

  /** Sorting direction of given items */
  @Prop() itemsSortDirection: "ascending" | "descending" = "descending";

  /** Event dates */
  @Prop() items: StzhEventinfoItemInput[] | string;
  private _items: StzhEventinfoItem[] = [];

  /**
   * Date adapter, for custom parsing/formatting.
   * Must be object with a `parse` function which accepts a `string` and returns a `Date`,
   * and a `format` function which accepts a `Date` and returns a `string`.
   */
  @Prop() dateAdapter: StzhLocaleAdapter;

  @Element() element: HTMLStzhEventinfoElement;

  @Watch("items")
  @Watch("dateAdapter")
  itemsWatcher() {
    if (!this.dateAdapter) {
      return;
    }

    const data: StzhEventinfoItemInput[] = typeof this.items === "string" ? JSON.parse(this.items) : this.items;

    if (!data) {
      return;
    }

    // sort direction
    const sortAscending = this.itemsSortDirection === "ascending";

    // convert start and end field to date
    this._items = data
      .map(event => ({
        ...event,
        start: event.start ? new Date(event.start) : undefined,
        end: event.end ? new Date(event.end) : undefined,
      }))
      .sort((a, b) => {
        try {
          const comparison = a.start.getTime() - b.start.getTime();
          return sortAscending ? comparison : -comparison;
        } catch (noStartTimeException) {
          return 1;
        }
      });

    // Grouping events by month and year
    const groupedItems: { [monthKey: string]: StzhEventinfoMonth } = this._items.reduce((acc, event) => {
      // Using padded month and year for better sorting
      const month = (event.start.getMonth() + 1).toString().padStart(2, "0");
      const monthYearKey = `${event.start.getFullYear()}-${month}`;

      if (!acc[monthYearKey]) {
        acc[monthYearKey] = {
          name: this.localization.$globals.monthNames[event.start.getMonth()] + " " + event.start.getFullYear(),
          events: [],
        } as StzhEventinfoMonth;
      }

      acc[monthYearKey].events.push(event);

      // Sort events within each month
      acc[monthYearKey].events.sort((a, b) => {
        const comparison = a.start.getTime() - b.start.getTime();
        return sortAscending ? comparison : -comparison;
      });

      return acc;
    }, {});

    this._itemsGroupedByMonth = Object.values(groupedItems);
  }

  private _itemsGroupedByMonth: StzhEventinfoMonth[] = [];
  private observer: MutationObserver;

  private init = () => {
    const locationTexts = this.element.querySelectorAll('stzh-text[slot="location"], [slot="location"] stzh-text');

    const additionalInfoTexts = this.element.querySelectorAll(
      'stzh-text[slot="additional-info"], [slot="additional-info"] stzh-text'
    );

    locationTexts.forEach(text => {
      setPropsIfNull(text, {
        color: "inherit",
        curve: "none",
      } as HTMLStzhTextElement);
    });

    additionalInfoTexts.forEach(text => {
      setPropsIfNull(text, {
        color: "inherit",
        curve: "none",
      } as HTMLStzhTextElement);
    });
  };

  private hasProperStartAndOrEndDate(startDate: Date | undefined, endDate: Date | undefined) {
    return !(!!startDate && startDate.getHours() === 0 && !endDate);
  }

  async componentWillLoad() {
    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "eventinfo");
    }

    if (!this.dateAdapter) {
      this.dateAdapter = window.stzhComponents.utils.createFormatParseAdapter(
        this.localization.$formats,
        this.localization.$globals
      );
    }

    this.itemsWatcher();
  }

  connectedCallback() {
    this.init();

    this.observer = new MutationObserver(this.init);
    this.observer.observe(this.element, {
      childList: true,
      subtree: true,
    });
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  render() {
    const classes = {
      "stzh-eventinfo": true,
    };

    const startDate = this.start && new Date(this.start);
    const endDate = this.end && new Date(this.end);

    const MonthHeading = `h${this.monthHeadingLevel}`;

    return (
      <Host>
        <div class={classes}>
          <div class="stzh-eventinfo__left">
            <div class="stzh-eventinfo__main-event-day-name">
              {this.localization.$globals.dayNamesShort[startDate.getDay()]}
            </div>
            <div class="stzh-eventinfo__main-event-day">
              {startDate.getDate() < 10 && "0"}
              {startDate.getDate()}
            </div>
            <div class="stzh-eventinfo__main-event-month">
              {this.localization.$globals.monthNamesShort[startDate.getMonth()]}
            </div>
          </div>

          <div class="stzh-eventinfo__right">
            <div>
              <div class="stzh-eventinfo__head">
                <div class="stzh-eventinfo__main-date">
                  <span>{this.localization.$globals.dayNames[startDate.getDay()]}</span>
                  <span>{this.dateAdapter.format(startDate, "date")}</span>
                  {this.hasProperStartAndOrEndDate(startDate, endDate) && (
                    <span>{this.dateAdapter.formatSpan(startDate, endDate, "time")}</span>
                  )}
                </div>

                {this.calendarUrl && (
                  <div class="stzh-eventinfo__main-event-link">
                    <stzh-link
                      href={this.calendarUrl}
                      label={this.calendarLabel || this.localization.addToCalendar}
                      size="inherit"
                    ></stzh-link>
                  </div>
                )}
              </div>

              <div class="stzh-eventinfo__text">
                <div class="stzh-eventinfo__location">
                  <slot name="location"></slot>
                </div>
                <div class="stzh-eventinfo__additional-info">
                  <slot name="additional-info"></slot>
                </div>
              </div>
            </div>

            {this._itemsGroupedByMonth.length > 0 && (
              <div class="stzh-eventinfo__details-wrapper">
                <stzh-details class="stzh-eventinfo__details">
                  <stzh-link class="stzh-eventinfo__details-link" icon="calendar" size="inherit">
                    <span class="stzh-eventinfo__details-link-expand">{this.localization.expandMoreDates}</span>
                    <span class="stzh-eventinfo__details-link-collapse">{this.localization.collapseMoreDates}</span>
                  </stzh-link>

                  <ul slot="content" class="stzh-eventinfo__month-list">
                    {this._itemsGroupedByMonth.map(groupedItems => (
                      <li class="stzh-eventinfo__month-list-item">
                        <MonthHeading class="stzh-eventinfo__heading">{groupedItems.name}</MonthHeading>

                        {groupedItems.events.length > 0 && (
                          <ul class="stzh-eventinfo__event-list">
                            {groupedItems.events.map(event => (
                              <li class="stzh-eventinfo__event-list-item">
                                <span class="stzh-eventinfo__event-datetime-wrapper">
                                  <span class="stzh-eventinfo__event-date-wrapper">
                                    <span class="stzh-eventinfo__event-weekday">
                                      {this.localization.$globals.dayNames[event.start.getDay()]}
                                    </span>
                                    <span class="stzh-eventinfo__event-date">
                                      {this.dateAdapter.format(event.start, "date")}
                                    </span>
                                  </span>
                                  <span class="stzh-eventinfo__event-time">
                                    {this.hasProperStartAndOrEndDate(event.start, event.end) &&
                                      this.dateAdapter.formatSpan(event.start, event.end, "time")}
                                  </span>
                                </span>
                                <span class="stzh-eventinfo__event-link">
                                  {event.calendarUrl && (
                                    <stzh-link
                                      href={event.calendarUrl}
                                      label={event.calendarLabel || this.localization.addToCalendar}
                                    ></stzh-link>
                                  )}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                </stzh-details>
              </div>
            )}
          </div>
        </div>
      </Host>
    );
  }
}
